// Rome API Types

export enum WorkspaceState {
  STARTING = 'starting',
  RUNNING = 'running',
  STOPPING = 'stopping',
  STOPPED = 'stopped',
  ERRORED = 'errored',
  TERMINATING = 'terminating',
  TERMINATED = 'terminated',
}

export interface RomeWorkspace {
  workspace_id: string;
  state: string;
  user_id: string;
  org_id: string;
  /** Organization display name - fetched from Auth0 on demand, not stored in Redis */
  org_name?: string | null;
  hostname: string;
  name?: string;
  /** antioch-sim version (e.g., "1.2.3"); empty for legacy workspaces */
  version?: string;
  /** Workspace image name (e.g., "acme-ros2"); empty/null means the central antioch-sim image */
  image?: string | null;
  /** User display name - fetched from Auth0 on demand, not stored in Redis */
  user_name?: string | null;
  /** User email - fetched from Auth0 on demand, not stored in Redis */
  user_email?: string | null;
  ip_address?: string;
  disk_name?: string;
  created_at?: number;
  starting_at?: number;
  running_at?: number;
  stopping_at?: number;
  stopped_at?: number;
  errored_at?: number;
  terminating_at?: number;
  terminated_at?: number;
  failure_reason?: string | null;
  /** Accumulated running time across all sessions (seconds) */
  total_runtime_seconds?: number;
  /** Number of times workspace has been started */
  session_count?: number;
  /** Unix timestamp when the workspace will auto-pause for scheduled node maintenance (null/0 if none) */
  scheduled_shutdown_at?: number | null;
}

export interface RomeWorkspacesResponse {
  data: RomeWorkspace[];
  message: string;
  status_code: number;
}

export interface RomeStartWorkspaceResponse {
  data: {
    workspace_id: string;
    name: string;
    version: string;
    image?: string;
    status: string;
  };
  message: string;
  status_code: number;
}

export type WorkspaceAuthPendingStatus = 'pending' | 'slow_down';

export interface WorkspaceAuthPendingResponse {
  data: {
    status: WorkspaceAuthPendingStatus;
    interval: number;
  };
  message: string;
  status_code: 202;
}

export type RomeWorkspaceStartResponse = RomeStartWorkspaceResponse | WorkspaceAuthPendingResponse;

export interface WorkspaceAuthStartResponse {
  data: {
    device_code: string;
    verification_uri: string;
    verification_uri_complete: string;
    user_code: string;
    expires_in: number;
    interval: number;
  };
  message: string;
  status_code: number;
}

// Workspace Version Types
export interface WorkspaceVersion {
  version: string;
  image_tag: string;
  image: string;
  released_at?: string | null;
}

export interface WorkspaceVersionsResponse {
  data: WorkspaceVersion[];
  message: string;
  status_code: number;
}

// Workspace Image Types
export interface WorkspaceImage {
  /** Opaque image name (e.g., "antioch-sim", "img-9f3c1a") */
  name: string;
  /**
   * Friendly customer name. Set by Rome only for the org that owns the image
   * or under a staff reveal; otherwise absent/null and the opaque `name` is
   * all we have. Render `display_name ?? name`.
   */
  display_name?: string | null;
  /** antioch-sim base semver, cross-checked against the image's antioch.sim.version label */
  version: string;
  /** Full tag (e.g., "staging-v1.2.3"); tags are per-environment */
  image_tag: string;
  /** Full repo path (e.g., "us-central1-docker.pkg.dev/at-<org>/images/acme-ros2") */
  repository: string;
  /** Which registry the entry came from */
  source: 'central' | 'org';
  /** GAR version createTime */
  released_at?: string | null;
  /** Whether this is the org's preselected image (per-org policy, falling back to antioch-sim) */
  default?: boolean;
}

export interface InvalidWorkspaceImage {
  name: string;
  image_tag: string;
  reason: string;
}

export interface WorkspaceImagesResponse {
  data: WorkspaceImage[];
  /** Disqualified tenant image tags with reasons; present only when include_invalid=true */
  invalid?: InvalidWorkspaceImage[];
  /** Set when the org's tenant image listing failed; central entries are still served */
  org_images_error?: string | null;
  message: string;
  status_code: number;
}

// Transformed types for UI consumption
export interface AntiochWorkspace {
  id: string;
  name: string;
  hostname: string;
  ipAddress?: string;
  createdAt: number;
  /** Timestamp when current session started running */
  runningAt?: number;
  /** Timestamp when workspace was stopped */
  stoppedAt?: number;
  /** Timestamp when workspace entered errored state */
  erroredAt?: number;
  /** antioch-sim version (e.g., "1.2.3"); empty for legacy workspaces */
  version?: string;
  /** Workspace image name; undefined means the central antioch-sim image */
  image?: string;
  /** Human-readable reason for last startup failure */
  failureReason?: string | null;
  /** Unix timestamp (seconds) when the workspace will auto-pause for scheduled node maintenance; 0/undefined if none */
  scheduledShutdownAt?: number;
  state: WorkspaceState;
}

// Admin workspace types (same structure as RomeWorkspace)
export type AdminWorkspaceInfo = RomeWorkspace;

export interface AdminWorkspacesResponse {
  message: string;
  data: RomeWorkspace[];
  status_code: number;
}

// Workspace Progress Types
export interface WorkspaceProgressStep {
  step: string;
  message: string;
  timestamp: number;
  status: 'in_progress' | 'completed' | 'failed';
}

export interface WorkspaceProgressResponse {
  data: {
    workspace_id: string;
    steps: WorkspaceProgressStep[];
  };
  message: string;
  status_code: number;
}

// Scenario Run Types
export enum ScenarioOutcome {
  SUCCESS = 'success',
  FAILURE = 'failure',
  ERROR = 'error',
  TIMEOUT = 'timeout',
  SKIPPED = 'skipped',
}

export interface ScenarioRunner {
  runner_id: string | null;
  runner_name: string | null;
  environment: Record<string, unknown> | null;
}

export interface ScenarioRun {
  run_id: string;
  started_at: string;
  completed_at: string;
  outcome: string;
  results: Record<string, unknown> | null;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  org_id: string;
  rrd_gcs_link: string | null;
  bundle_gcs_link: string | null;
  recording_size_bytes: number | null;
  scenario_name: string;
  description: string | null;
  tags: string[] | null;
  parameters: Record<string, unknown> | null;
  runner_id: string | null;
  runner_name: string | null;
  runner_environment: Record<string, unknown> | null;
  duration_ms: number | null;
  suite_name?: string | null;
  suite_run?: string | null;
}

export interface CustomFilter {
  type: 'params' | 'results';
  key: string;
  op: '=' | '~' | '>' | '<';
  value: string;
}

export interface QueryScenariosRequest {
  custom_filters?: CustomFilter[] | null;
  date_from?: string | null;
  date_to?: string | null;
  limit?: number;
  offset?: number;
  /** Opaque pagination cursor from a previous response's `next_cursor`. When set, supersedes `offset`. */
  cursor?: string | null;
  match_substrings?: boolean;
  outcome?: ScenarioOutcome | null;
  search?: string | null;
  /** Terms the scenario name must contain (all must match). */
  search_terms?: string[] | null;
  /** Terms the scenario name must not contain (any match excludes the run). */
  exclude_search_terms?: string[] | null;
  suite?: string | null;
  suite_run?: string | null;
  tags?: string[] | null;
  /** Tags to exclude; a run carrying any of them is dropped. */
  exclude_tags?: string[] | null;
  /** OR-of-AND tag groups; each entry is `&`-split into an ANDed group, groups ORed. */
  tag_groups?: string[] | null;
  /** OR-of-AND scenario-name groups; same `&`/OR semantics as tag_groups. */
  name_term_groups?: string[] | null;
  scenario_name?: string | null;
  user_id?: string | null;
  /** User ids; a run by any of them matches (ORed). */
  user_ids?: string[] | null;
  /** User ids to exclude; a run by any of them is dropped. */
  exclude_user_ids?: string[] | null;
  /** Exact scenario names to exclude (per-scenario hide). */
  exclude_scenario_names?: string[] | null;
}

export interface GetScenarioRunResponse {
  run: ScenarioRun;
  message: string;
  status_code: number;
}

export interface QueryScenariosResponse {
  runs: ScenarioRun[];
  total: number;
  /** Cursor for the next page; null when there are no more results. */
  next_cursor: string | null;
  message: string;
  status_code: number;
}

export interface SuiteSummary {
  suite_tag: string;
  name: string;
  scenario_count: number;
  latest_pass_rate: number;
  pass_rate_history: number[];
  total_runs: number;
  suite_run_count?: number;
  last_suite_run_at?: string;
  last_suite_run_user?: string | null;
  /** Auth0 user id of the latest suite-run user -- used to filter by user
   *  (the display name in last_suite_run_user is not a valid filter value). */
  last_suite_run_user_id?: string | null;
  last_suite_run_status?: 'passed' | 'failed' | 'mixed';
  /** @deprecated Legacy API field -- prefer suite_run_count */
  instance_count?: number;
  /** @deprecated Legacy API field -- prefer last_suite_run_at */
  last_instance_at?: string;
  /** @deprecated Legacy API field -- prefer last_suite_run_user */
  last_instance_user?: string | null;
  /** @deprecated Legacy API field -- prefer last_suite_run_status */
  last_instance_status?: 'passed' | 'failed' | 'mixed';
}

export interface QuerySuitesResponse {
  suites: SuiteSummary[];
  message: string;
  status_code: number;
}

export interface DeleteScenariosResponse {
  deleted_count: number;
  message: string;
  status_code: number;
}

export interface DownloadUrlsResponse {
  rrd_url: string | null;
  rrd_filename: string | null;
  bundle_url: string | null;
  bundle_filename: string | null;
  message: string;
  status_code: number;
}

// Scenario Suggestions
export interface ScenarioSuggestion {
  value: string;
  count: number;
  user_name?: string | null;
  user_email?: string | null;
}

export interface ScenarioSuggestContext {
  custom_filters?: CustomFilter[];
  date_from?: string;
  date_to?: string;
  outcome?: string;
  tags?: string[];
  scenario_name?: string;
  user_id?: string;
  suite?: string;
  suite_run?: string;
}

export interface ScenarioSuggestResponse {
  suggestions: ScenarioSuggestion[];
  message: string;
  status_code: number;
}

// Service Token Types
export interface ServiceTokens {
  workspace_token: string;
  streaming_token: string;
}

export interface MintTokensResponse {
  data: ServiceTokens;
  message: string;
  status_code: number;
}

// Asset Types
export interface AssetVersion {
  created_at: string;
  full_path: string;
  size_bytes: number;
  updated_at: string;
  version: string;
  metadata: Record<string, string>;
  preview_url: string | null;
  description: string | null;
  tags: string[];
  uploaded_by: string | null;
}

export interface AssetListItem {
  created_at: string;
  name: string;
  updated_at: string;
  versions: AssetVersion[];
  /**
   * True when the asset is owned by the Antioch shelf sync script.
   * Derived server-side from ``source=shelf`` GCS blob metadata written by
   * the sync script on every push — not from the asset name.
   * Shelf-managed assets must not surface Rename / Move / Delete controls
   * in the webapp. Defaults to false on responses from older Rome instances.
   */
  is_shelf_managed?: boolean;
}

export interface AssetListResponse {
  data: AssetListItem[];
  message: string;
  status_code: number;
}

// SSH Key Types
export interface SSHKey {
  id: string;
  name: string;
  public_key: string;
  created_at: number;
}

export interface SSHKeysListResponse {
  data: SSHKey[];
  message: string;
  status_code: number;
}

export interface SSHKeyResponse {
  data: SSHKey;
  message: string;
  status_code: number;
}

// Usage Statistics Types
//
// `vwh` is billable workspace-hours (members + admins). `vwh_support` is
// the parallel support-staff total, present only when the request opts
// in via `include_support=true` (the Usage tab does, the home page does
// not — so the home page sees `total_vwh` as the customer-facing total).
export interface DailyUsage {
  date: string;
  vwh: number;
  vwh_support?: number | null;
}

export interface UsageData {
  org_id: string;
  start_time: number;
  end_time: number;
  total_vwh: number;
  total_vwh_support?: number | null;
  daily_breakdown: DailyUsage[];
}

export interface UsageResponse {
  data: UsageData;
  message: string;
  status_code: number;
}

// Workspace Limits Types
export interface WorkspaceLimits {
  max_workspaces: number;
  active_workspaces: number;
  available_workspaces: number;
}

export interface WorkspaceLimitsResponse {
  data: WorkspaceLimits;
  message: string;
  status_code: number;
}

// Organization Member Types
export interface OrgMember {
  user_id: string;
  email: string | null;
  name: string | null;
  picture: string | null;
  role: 'team_admin' | 'team_member' | 'support_staff' | null;
}

export interface OrgMembersResponse {
  data: OrgMember[];
  message: string;
  status_code: number;
}

export type OrgRole = 'team_admin' | 'team_member' | 'support_staff';

export interface CurrentUser {
  user_id: string;
  email: string | null;
  name: string | null;
  role: OrgRole | null;
}

export interface CurrentUserResponse {
  data: CurrentUser;
  message: string;
  status_code: number;
}

export interface InviteMemberResponse {
  message: string;
  status_code: number;
}

export interface RemoveMemberResponse {
  message: string;
  status_code: number;
}

export interface UpdateRoleResponse {
  message: string;
  status_code: number;
}

// Pending Invitation Types
export interface PendingInvite {
  id: string;
  invitee_email: string | null;
  inviter_name: string | null;
  role: OrgRole | null;
  created_at: string | null;
  expires_at: string | null;
  invitation_url: string | null;
}

export interface PendingInvitesResponse {
  data: PendingInvite[];
  message: string;
  status_code: number;
}

export interface RevokeInviteResponse {
  message: string;
  status_code: number;
}

// Admin GPU Status Types
export type ClusterProvider = 'gke' | 'nebius';

export interface GpuDistributionEntry {
  /** Capacity source: 'reservation:<name>', 'ondemand', or 'nebius-pool'. */
  source: string;
  region: string;
  gpu_nodes: number;
}

export interface OrgGpuRow {
  org_id: string;
  org_name: string | null;
  provider: ClusterProvider;
  workspaces_running: number;
  /** GPU nodes currently booted (committed capacity). */
  gpu_nodes_active: number;
  /** GPUs doing active work (= running workspaces). */
  gpus_active: number;
  /** Sum of GPU node-pool autoscaler minimums (warm floor). */
  gpus_min: number | null;
  /** Effective max GPUs/workspaces: min(node-pool autoscaler ceiling, Auth0 cap). */
  gpus_max: number | null;
  /** Auth0 max_rtx6000_gpus — the concurrent-workspace cap Rome enforces at start. */
  gpus_cap: number | null;
  distribution: GpuDistributionEntry[];
  error: string | null;
}

export interface ReservationRow {
  name: string;
  region: string;
  /** GPUs committed (nodes booted against the reservation). */
  gpus_used: number;
  /** Reservation capacity (max GPUs). */
  gpus_max: number;
}

export interface ProviderGpuRow {
  provider: ClusterProvider;
  workspaces_running: number;
  reservations: ReservationRow[];
  error: string | null;
}

export interface GpuStatusData {
  generated_at: number;
  stale: boolean;
  orgs: OrgGpuRow[];
  providers: ProviderGpuRow[];
}

export interface GpuStatusResponse {
  data: GpuStatusData;
  message: string;
  status_code: number;
}
